import { type Failure, RequestFailed, readJson, request } from './request'

/**
 * The assistants that can reach this space, as the service describes them.
 *
 * The whole area arrives in **one** answer, like the external systems do, and for the same reason:
 * a space holds a handful of these and each carries a short log, so paging something somebody looks
 * at twice a semester would be machinery in place of a list.
 *
 * **A token comes back exactly once**, from {@link issueAccess}, and from nothing else. There is
 * deliberately no field for one on {@link Access} and no call that answers with one — the promise
 * made on the screen ("this is the only time it is shown") is kept by there being nothing left to
 * show, not by a component choosing not to draw it.
 */

/** How an access came to be — which is the same as saying where its token went. */
export type AccessOrigin = 'connected' | 'byHand'

/** How one call through an access went. */
export type UseOutcome = 'answered' | 'refused' | 'failed'

/**
 * One call made through an access: which tool, when, and how it went.
 *
 * What was asked for is deliberately absent, here and in the service. This answers "was something
 * done here that was not me", and that question needs no search terms.
 */
export interface Use {
  readonly at: string
  readonly tool: string
  readonly outcome: UseOutcome
}

export interface Access {
  readonly id: string
  /** What somebody calls it. Telling two of them apart is the whole point of the field. */
  readonly name: string
  readonly origin: AccessOrigin
  /** Whether it may change anything, or may only read. */
  readonly mayWrite: boolean
  /** What the program called itself when it connected. Empty for one made by hand. */
  readonly clientName: string
  readonly createdAt: string
  /** When it was last used at all, or `null` while it never has been. */
  readonly lastUsedAt: string | null
  /** The last few calls, newest first. Older ones fall away; nothing here is a full history. */
  readonly uses: readonly Use[]
}

export interface AssistantAccess {
  /**
   * The address to give an assistant. It carries this space, so two spaces are two addresses.
   *
   * Answered by the service rather than assembled here: what a space's address is must not be a
   * thing the browser decides.
   */
  readonly endpoint: string
  /**
   * Where this space's library answers WebDAV, so it can be mounted as a drive.
   *
   * The same access opens both — one is a protocol an assistant speaks, the other a protocol a file
   * manager speaks — which is why the two addresses arrive together and are shown together.
   */
  readonly mount: string
  readonly accesses: readonly Access[]
}

/** A freshly made access, and the token that will not be shown a second time. */
export interface IssuedAccess {
  readonly access: Access
  readonly tokenShownOnlyNow: string
}

/** What a program asked for, so the page can say who is asking before somebody agrees. */
export interface AskedFor {
  readonly clientName: string
  /** Always true, and said out loud: it is the program's own word about itself, not MyLib's. */
  readonly asItCallsItself: boolean
  readonly alreadyAgreed: boolean
}

const ASSISTANTS = '/api/assistants'
const OAUTH = '/api/oauth'

export async function fetchAssistantAccess(signal?: AbortSignal): Promise<AssistantAccess> {
  return ask<AssistantAccess>(ASSISTANTS, signal)
}

/**
 * Makes an access by hand and answers with its token, once.
 *
 * For the programs that cannot be sent through a browser — a terminal, an editor's assistant, the
 * tool one tries things out with. Everything else connects itself and never needs this.
 */
export async function issueAccess(name: string, mayWrite: boolean): Promise<IssuedAccess> {
  return (await change(ASSISTANTS, 'POST', { name, mayWrite })) as IssuedAccess
}

export async function changeAccess(
  id: string,
  values: { name?: string; mayWrite?: boolean },
): Promise<Access> {
  return (await change(`${ASSISTANTS}/${encodeURIComponent(id)}`, 'PUT', values)) as Access
}

/** Revokes it. It stops working at the very next call, and nothing it wrote is touched. */
export async function revokeAccess(id: string): Promise<void> {
  await change(`${ASSISTANTS}/${encodeURIComponent(id)}`, 'DELETE')
}

/** What a program is asking for. Needs a session — this is a person deciding, in their own browser. */
export async function whatIsAsked(request: string, signal?: AbortSignal): Promise<AskedFor> {
  return ask<AskedFor>(`${OAUTH}/authorize/${encodeURIComponent(request)}`, signal)
}

/**
 * Says yes or no, and answers with where the browser goes next.
 *
 * The address that comes back belongs to the program that asked, and the browser is sent there by
 * the page rather than by the service — so the last thing that happens is a navigation somebody's
 * own click caused.
 */
export async function decide(
  request: string,
  answer: { approve: boolean; name?: string; mayWrite?: boolean },
): Promise<string> {
  const decided = (await change(
    `${OAUTH}/authorize/${encodeURIComponent(request)}`,
    'POST',
    answer,
  )) as {
    redirectTo: string
  }
  return decided.redirectTo
}

const REFUSALS = [400, 404, 409, 503] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-access': 'That access is not there any more.',
  'unusable-access-name':
    'An access needs a name — without one there is no way to tell which of them to revoke.',
  /*
    The two the consent page can meet, and they are deliberately different sentences. One is a link
    that has been sitting in a tab since yesterday; the other is the second of two tabs, where the
    honest thing to say is that it already worked.
  */
  'no-such-request':
    'That request is no longer waiting for an answer. Connect again from the assistant.',
  'already-agreed': 'This was already answered — the assistant has its access.',
}

async function ask<T>(address: string, signal?: AbortSignal): Promise<T> {
  const response = await request(address, { signal, expect: [...REFUSALS] })
  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }
  return readJson<T>(response)
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: known === undefined && status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    /* None of these gets better by being tried again: each is as true the second time as the first. */
    retryable: false,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
